import type { ReactNode } from 'react';
import { Image } from 'antd';
import type { CommentMention } from '@lens/shared';
import { parseWidthFromSrc } from '../../shared/fields/resizableImage.ts';

/** Минимум, нужный логике упоминаний; реальный SpaceMember совместим по структуре. */
export interface MentionMember {
  userId: string;
  login: string;
  displayName: string;
}

/** Группа пространства в подсказке упоминаний. */
export interface MentionGroup {
  id: string;
  name: string;
  memberCount: number;
}

/**
 * Кого можно упомянуть: человека, группу пользователей пространства или всех
 * участников. Токен в тексте у всех троих один и тот же по форме — `@что-то`
 * без пробелов; различает их этот тип.
 */
export type MentionTarget =
  | ({ kind?: 'user' } & MentionMember)
  | { kind: 'group'; groupId: string; displayName: string }
  | { kind: 'all'; displayName: string };

/** Токен → кого он обозначает. Ключ — то, что стоит в тексте после «@». */
export type MentionIndex = Map<string, MentionTarget>;

// Совпадает с тем, как AntD <Mentions> вставляет упоминание: «@<login>».
export const MENTION_RE = /@([^\s@]+)/g;

/** Токен «все участники» — им зовут всё пространство разом. */
export const MENTION_ALL_TOKEN = 'все';

/**
 * Токен группы — её название без пробелов («Команда Android» →
 * «Команда_Android»). Пробел разорвал бы упоминание пополам, а имя в тексте
 * читается и без подсветки — в отличие от id.
 */
export function groupMentionToken(name: string): string {
  return name.trim().replace(/\s+/g, '_');
}

/**
 * Строит указатель упоминаний: люди по логину, группы по названию, «все».
 * Логин сильнее одноимённой группы — человек с таким логином точно есть,
 * а группу можно переименовать.
 */
export function buildMentionIndex(
  members: MentionMember[],
  groups: MentionGroup[] = [],
): MentionIndex {
  const index: MentionIndex = new Map();
  index.set(MENTION_ALL_TOKEN, { kind: 'all', displayName: MENTION_ALL_TOKEN });
  for (const group of groups) {
    index.set(groupMentionToken(group.name), {
      kind: 'group',
      groupId: group.id,
      displayName: group.name,
    });
  }
  for (const member of members) index.set(member.login, member);
  return index;
}

/**
 * Жадно сопоставляет самый длинный префикс токена с указателем упоминаний.
 * Возвращает адресата и «хвост» (например, замыкающую пунктуацию «@alice,»),
 * который нужно отрисовать как обычный текст. null — если префикс не совпал.
 *
 * `member` заполняется только для человека — вызовы, которым нужен именно
 * участник, работают как раньше.
 */
export function resolveMention(
  token: string,
  index: MentionIndex,
): { target: MentionTarget; member?: MentionMember; rest: string } | null {
  let candidate = token;
  while (candidate.length > 0) {
    const target = index.get(candidate);
    if (target) {
      const rest = token.slice(candidate.length);
      return isUserTarget(target) ? { target, member: target, rest } : { target, rest };
    }
    candidate = candidate.slice(0, -1);
  }
  return null;
}

function isUserTarget(target: MentionTarget): target is { kind?: 'user' } & MentionMember {
  return (target as { userId?: string }).userId !== undefined;
}

/** Ключ для дедупликации: одного адресата не отправляем дважды. */
function mentionKey(target: MentionTarget): string {
  if (isUserTarget(target)) return `user:${target.userId}`;
  return target.kind === 'group' ? `group:${target.groupId}` : 'all';
}

/**
 * Извлекает уникальные упоминания из текста для отправки на бэкенд. Группу и
 * «всех» отправляем как есть — разворачивать их в людей будет сервер, на момент
 * отправки комментария.
 */
export function extractMentions(text: string, index: MentionIndex): CommentMention[] {
  const seen = new Set<string>();
  const found: CommentMention[] = [];
  MENTION_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MENTION_RE.exec(text)) !== null) {
    const resolved = resolveMention(match[1] ?? '', index);
    if (!resolved) continue;
    const key = mentionKey(resolved.target);
    if (seen.has(key)) continue;
    seen.add(key);
    const target = resolved.target;
    if (isUserTarget(target)) {
      found.push({ userId: target.userId, displayName: target.displayName });
    } else if (target.kind === 'group') {
      found.push({ kind: 'group', groupId: target.groupId, displayName: target.displayName });
    } else {
      found.push({ kind: 'all', displayName: target.displayName });
    }
  }
  return found;
}

// Один из: картинка `![alt](url)` (группы 1–2), жирный `**текст**` (группа 3),
// инлайн-код `` `текст` `` (группа 4), голый URL (группа 5), упоминание `@login`
// (группа 6). Порядок альтернатив = приоритет: картинку и код проверяем раньше,
// чтобы `**` и `@` внутри URL/кода не перехватывались, а URL — раньше упоминания,
// чтобы `@` внутри ссылки не рвал её.
const TOKEN_RE = /!\[([^\]]*)\]\(([^)\s]+)\)|\*\*([^*\n]+)\*\*|`([^`\n]+)`|(https?:\/\/[^\s]+)|@([^\s@]+)/g;

/**
 * Отрезает от «хвоста» URL замыкающую пунктуацию, которая почти наверняка не
 * часть адреса (`https://x.ru/a.html.` → ссылка + «.»). Скобку `)` отрезаем,
 * только если она не парная (в URL встречаются `(...)`, напр. Wikipedia).
 */
function splitTrailingPunct(url: string): { link: string; rest: string } {
  let end = url.length;
  while (end > 0) {
    const ch = url[end - 1] ?? '';
    if ('.,;:!?»"\'’'.includes(ch)) {
      end -= 1;
      continue;
    }
    if (ch === ')') {
      const link = url.slice(0, end);
      const opens = (link.match(/\(/g) ?? []).length;
      const closes = (link.match(/\)/g) ?? []).length;
      if (closes > opens) {
        end -= 1;
        continue;
      }
    }
    break;
  }
  return { link: url.slice(0, end), rest: url.slice(end) };
}

/**
 * Рендерит тело комментария: подсвечивает «@login» известных участников как
 * «@ИмяФамилия», отрисовывает вставленные картинки `![alt](url)` (ширина — из
 * query `?w=`, как в полях описания; см. resizableImage.ts) и лёгкий инлайн-
 * markdown — `**жирный**` и `` `код` ``. Остальной текст остаётся как есть
 * (контейнер задаёт `white-space: pre-wrap`).
 */
export function renderCommentBody(body: string, byLogin: MentionIndex): ReactNode {
  // Схлопываем пустые строки (импорт из Nexus кладёт «текст\n\n— автор · дата»):
  // при white-space: pre-wrap двойной перенос рисует зияющую пустую строку и
  // лента выглядит рвано. Меняем только отображение — в хранилище тело как есть,
  // правка открывает оригинал.
  body = body.replace(/\n[ \t]*(?:\n[ \t]*)+/g, '\n').trim();
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  TOKEN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN_RE.exec(body)) !== null) {
    const [whole, imgAlt, imgUrl, boldText, codeText, urlToken, mentionToken] = match;

    if (imgUrl !== undefined) {
      // Картинка.
      if (match.index > lastIndex) nodes.push(body.slice(lastIndex, match.index));
      const { src, width } = parseWidthFromSrc(imgUrl);
      nodes.push(
        <div key={`img-${key++}`} style={{ margin: '6px 0' }}>
          <Image
            src={src}
            alt={imgAlt ?? ''}
            {...(width ? { width } : null)}
            style={{
              maxWidth: '100%',
              maxHeight: 360,
              borderRadius: 8,
              cursor: 'zoom-in',
            }}
            preview={{ mask: 'Открыть' }}
          />
        </div>,
      );
      lastIndex = match.index + whole.length;
      continue;
    }

    if (boldText !== undefined) {
      // Жирный `**текст**`.
      if (match.index > lastIndex) nodes.push(body.slice(lastIndex, match.index));
      nodes.push(<strong key={`b-${key++}`}>{boldText}</strong>);
      lastIndex = match.index + whole.length;
      continue;
    }

    if (codeText !== undefined) {
      // Инлайн-код `` `текст` ``.
      if (match.index > lastIndex) nodes.push(body.slice(lastIndex, match.index));
      nodes.push(
        <code
          key={`c-${key++}`}
          style={{
            padding: '1px 5px',
            borderRadius: 4,
            fontSize: '0.9em',
            background: 'var(--color-fill-secondary, rgba(0,0,0,0.06))',
            fontFamily:
              "'SF Mono', 'Menlo', 'Consolas', 'Liberation Mono', monospace",
          }}
        >
          {codeText}
        </code>,
      );
      lastIndex = match.index + whole.length;
      continue;
    }

    if (urlToken !== undefined) {
      // Голый URL → кликабельная ссылка (замыкающая пунктуация — обычным текстом).
      if (match.index > lastIndex) nodes.push(body.slice(lastIndex, match.index));
      const { link, rest } = splitTrailingPunct(urlToken);
      nodes.push(
        <a
          key={`link-${key++}`}
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--color-primary)', wordBreak: 'break-all' }}
        >
          {link}
        </a>,
      );
      if (rest) nodes.push(rest);
      lastIndex = match.index + whole.length;
      continue;
    }

    // Упоминание: человек, группа пользователей или «все».
    const resolved = resolveMention(mentionToken ?? '', byLogin);
    if (!resolved) continue; // не участник — оставляем как обычный текст
    if (match.index > lastIndex) nodes.push(body.slice(lastIndex, match.index));
    nodes.push(
      <span
        key={`mention-${key++}`}
        className="comment-mention"
        style={{ color: 'var(--color-primary)', fontWeight: 500 }}
      >
        @{resolved.target.displayName}
      </span>,
    );
    if (resolved.rest) nodes.push(resolved.rest);
    lastIndex = match.index + whole.length;
  }
  if (lastIndex < body.length) nodes.push(body.slice(lastIndex));
  return nodes.length > 0 ? nodes : body;
}
